import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  Spinner,
  Toast,
  ToastTitle,
  makeStyles,
  useToastController,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { invalidateContextHelpCache } from '../components/FieldHelp.js';
import { listContextHelp, updateContextHelp, type ContextHelpText } from '../api/contextHelp.js';
import { ApiClientError } from '../api/client.js';

/**
 * Editor de textos de ayuda contextual — SOLO staff Ecosoft (Soporte/CSM).
 * Spec 2026-07-02: los 4 bloques (Qué es / Cómo llenarlo / Ejemplo / Afecta)
 * se editan sin deploy; el vocabulario de chips es cerrado; los textos nunca
 * exponen %, rangos ni fórmulas de FACTORES (advertencia suave abajo).
 */

const CHIPS = ['Mano de obra', 'Equipo', 'Materiales', 'Indirectos', 'Normativa', 'APUs de referencia'] as const;

const SECCION_LABELS: Record<string, string> = {
  normativa: 'Tipo de obra y normativa',
  ubicacion: 'Ubicación y condiciones ambientales',
  sitio: 'Sitio y operación',
  general: 'General',
};

/** Detecta porcentajes que PARECEN de factores (número+%). Aviso suave, no bloqueo. */
function pctWarning(...texts: string[]): boolean {
  return texts.some((t) => /\d\s*%/.test(t));
}

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-page)' },
  container: { maxWidth: '1080px', margin: '0 auto', padding: '24px 28px' },
  title: { fontFamily: 'var(--font-display)', fontSize: '23px', fontWeight: 700, margin: '0 0 4px' },
  sub: { color: 'var(--text-secondary)', fontSize: '13px', margin: '0 0 20px' },
  seccion: {
    fontSize: '11px', fontWeight: 700, letterSpacing: '1.1px', textTransform: 'uppercase',
    color: 'var(--text-tertiary)', margin: '22px 0 10px',
  },
  row: {
    display: 'flex', alignItems: 'flex-start', gap: '14px',
    background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)', padding: '13px 16px', marginBottom: '8px',
  },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitle: { fontWeight: 600, fontSize: '14px' },
  rowText: { color: 'var(--text-secondary)', fontSize: '12.5px', marginTop: '3px' },
  rowMeta: { color: 'var(--text-tertiary)', fontSize: '11px', marginTop: '5px', fontFamily: 'var(--font-mono)' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' },
  chip: {
    padding: '2px 8px', borderRadius: 'var(--radius-circular)',
    background: 'var(--accent-weak)', color: 'var(--accent)', fontSize: '11px', fontWeight: 600,
  },
  formGrid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  lbl: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' },
});

export function StaffContextHelpPage() {
  const styles = useStyles();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [texts, setTexts] = useState<ContextHelpText[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContextHelpText | null>(null);
  const [draft, setDraft] = useState<ContextHelpText | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listContextHelp().then(setTexts).catch((e) => setError(e instanceof Error ? e.message : 'Error al cargar'));
  }, []);

  const bySeccion = useMemo(() => {
    const m = new Map<string, ContextHelpText[]>();
    for (const t of texts ?? []) {
      const arr = m.get(t.seccion) ?? [];
      arr.push(t);
      m.set(t.seccion, arr);
    }
    return m;
  }, [texts]);

  function openEdit(t: ContextHelpText) {
    setEditing(t);
    setDraft({ ...t });
  }

  async function onSave() {
    if (!draft || !editing) return;
    setSaving(true);
    try {
      const updated = await updateContextHelp(editing.key, {
        title: draft.title,
        queEs: draft.queEs,
        comoLlenarlo: draft.comoLlenarlo,
        ejemplo: draft.ejemplo,
        afecta: draft.afecta,
      });
      setTexts((prev) => (prev ?? []).map((t) => (t.key === updated.key ? updated : t)));
      invalidateContextHelpCache();
      setEditing(null);
      dispatchToast(<Toast><ToastTitle>Texto de ayuda guardado</ToastTitle></Toast>, { intent: 'success', timeout: 3000 });
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'Error al guardar'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 5000 },
      );
    } finally {
      setSaving(false);
    }
  }

  const crumbs = [
    { label: 'Inicio', to: '/projects' },
    { label: 'Ayuda contextual (staff)' },
  ];

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={crumbs} />
      <div className={styles.container}>
        <h1 className={styles.title}>Textos de ayuda contextual</h1>
        <p className={styles.sub}>
          Guías de los campos del Contexto de proyecto. Los cambios se publican al guardar, sin deploy.
          Regla: nunca escribas porcentajes, rangos ni fórmulas de factores — eso vive solo en Calibración.
        </p>

        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        {!texts && !error && <Spinner label="Cargando textos…" />}

        {[...bySeccion.entries()].map(([seccion, items]) => (
          <div key={seccion}>
            <div className={styles.seccion}>{SECCION_LABELS[seccion] ?? seccion}</div>
            {items.map((t) => (
              <div key={t.key} className={styles.row}>
                <div className={styles.rowBody}>
                  <div className={styles.rowTitle}>{t.title}</div>
                  <div className={styles.rowText}>{t.queEs}</div>
                  <div className={styles.chips}>
                    {t.afecta.map((c) => <span key={c} className={styles.chip}>{c}</span>)}
                  </div>
                  <div className={styles.rowMeta}>
                    {t.key}{t.updatedBy ? ` · editado ${new Date(t.updatedAt).toLocaleDateString('es-MX')}` : ''}
                  </div>
                </div>
                <Button size="small" appearance="secondary" onClick={() => openEdit(t)}>Editar</Button>
              </div>
            ))}
          </div>
        ))}
      </div>

      <Dialog open={editing !== null} onOpenChange={(_, d) => { if (!d.open) setEditing(null); }}>
        <DialogSurface style={{ maxWidth: 640 }}>
          <DialogBody>
            <DialogTitle>Editar ayuda — {editing?.title}</DialogTitle>
            <DialogContent>
              {draft && (
                <div className={styles.formGrid}>
                  <div>
                    <span className={styles.lbl}>Título</span>
                    <Input style={{ width: '100%' }} value={draft.title} onChange={(_e, d) => setDraft({ ...draft, title: d.value })} />
                  </div>
                  <div>
                    <span className={styles.lbl}>Qué es</span>
                    <Textarea style={{ width: '100%' }} resize="vertical" value={draft.queEs} onChange={(_e, d) => setDraft({ ...draft, queEs: d.value })} />
                  </div>
                  <div>
                    <span className={styles.lbl}>Cómo llenarlo</span>
                    <Textarea style={{ width: '100%' }} resize="vertical" value={draft.comoLlenarlo} onChange={(_e, d) => setDraft({ ...draft, comoLlenarlo: d.value })} />
                  </div>
                  <div>
                    <span className={styles.lbl}>Ejemplo</span>
                    <Textarea style={{ width: '100%' }} resize="vertical" value={draft.ejemplo} onChange={(_e, d) => setDraft({ ...draft, ejemplo: d.value })} />
                  </div>
                  <div>
                    <span className={styles.lbl}>Afecta (vocabulario cerrado)</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}>
                      {CHIPS.map((chip) => (
                        <Checkbox
                          key={chip}
                          label={chip}
                          checked={draft.afecta.includes(chip)}
                          onChange={(_e, d) =>
                            setDraft({
                              ...draft,
                              afecta: d.checked
                                ? [...draft.afecta, chip]
                                : draft.afecta.filter((c) => c !== chip),
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                  {pctWarning(draft.queEs, draft.comoLlenarlo, draft.ejemplo) && (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        El texto contiene un porcentaje. Verifica que NO sea un porcentaje, rango o
                        fórmula de un factor del motor (regla de la especificación); los % descriptivos
                        (ej. &quot;humedad mayor a 80%&quot;) sí están permitidos.
                      </MessageBarBody>
                    </MessageBar>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button appearance="primary" disabled={saving || !draft || draft.afecta.length === 0} onClick={() => void onSave()}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
